import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";
import { db, isFirebaseConfigured } from "../firebase";
import { BRANCH_ADDRESS_TEXT, SCHOOL_HOURS } from "../data/siteContent";

const EVENT_OPTIONS = [
  { value: "event1", label: "Bren woods Apartments", prefix: "A" },
  { value: "event2", label: "Mahaveer Ranches", prefix: "B" },
];

function readLocalItems(name, fallback = []) {
  return JSON.parse(localStorage.getItem(name) || "null") || fallback;
}

function isOfflineFirestoreError(error) {
  return (
    error?.code === "unavailable" ||
    error?.message?.toLowerCase().includes("client is offline")
  );
}

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error("Unable to read the selected image."));
    reader.readAsDataURL(file);
  });
}

export const defaultContactDetails = {
  phone: "+91-7618759333",
  email: "preethi@seekersbay.in",
  address: BRANCH_ADDRESS_TEXT,
  hours: `${SCHOOL_HOURS.statusLabel}: ${SCHOOL_HOURS.openToday} | ${SCHOOL_HOURS.schoolTime} | ${SCHOOL_HOURS.sunday}`,
  heroTitle: "Get in touch with our caring team today",
  heroDescription:
    "We're here to listen, guide, and support your child's learning journey. Reach out to our friendly team for any questions, admissions details, or assistance you need.",
  cityLabel: "Bengaluru",
  socials: [
    { label: "Pinterest", icon: "P", href: "#" },
    { label: "X", icon: "X", href: "#" },
    { label: "Facebook", icon: "f", href: "#" },
    { label: "Instagram", icon: "I", href: "#" },
  ],
};

const DEFAULT_ADMIN_NOTIFICATION_EMAIL = "preethi.ananth@gmail.com";

export const fallbackGalleryItems = [
  {
    id: "fallback-1",
    title: "Fun Activities",
    category: "activities",
    mediaType: "image",
    gradient: "from-yellow-300 to-orange-400",
    emoji: "🎨",
  },
  {
    id: "fallback-2",
    title: "Classroom Fun",
    category: "classroom",
    mediaType: "image",
    gradient: "from-teal-300 to-cyan-400",
    emoji: "🏫",
  },
  {
    id: "fallback-3",
    title: "Learning Time",
    category: "learning",
    mediaType: "image",
    gradient: "from-pink-300 to-rose-400",
    emoji: "📚",
  },
  {
    id: "fallback-4",
    title: "Outdoor Play",
    category: "activities",
    mediaType: "image",
    gradient: "from-lime-300 to-green-400",
    emoji: "🌿",
  },
  {
    id: "fallback-5",
    title: "Happy Kids",
    category: "kids",
    mediaType: "image",
    gradient: "from-sky-300 to-blue-400",
    emoji: "😄",
  },
  {
    id: "fallback-6",
    title: "Art Session",
    category: "activities",
    mediaType: "image",
    gradient: "from-violet-300 to-purple-400",
    emoji: "🖌️",
  },
];

function normalizeGalleryItem(item) {
  if (!item) {
    return item;
  }

  return {
    ...item,
    mediaType: item.mediaType || (item.videoUrl || item.embedUrl ? "video" : "image"),
  };
}

function extractYouTubeVideoId(value) {
  const input = String(value || "").trim();

  if (!input) {
    return "";
  }

  const shortMatch = input.match(/(?:youtu\.be\/)([A-Za-z0-9_-]{11})/);
  if (shortMatch?.[1]) {
    return shortMatch[1];
  }

  const watchMatch = input.match(/[?&]v=([A-Za-z0-9_-]{11})/);
  if (watchMatch?.[1]) {
    return watchMatch[1];
  }

  const embedMatch = input.match(/(?:youtube\.com\/embed\/)([A-Za-z0-9_-]{11})/);
  if (embedMatch?.[1]) {
    return embedMatch[1];
  }

  const shortsMatch = input.match(/(?:youtube\.com\/shorts\/)([A-Za-z0-9_-]{11})/);
  if (shortsMatch?.[1]) {
    return shortsMatch[1];
  }

  return "";
}

export function formatTimestamp(value) {
  if (!value) {
    return "-";
  }

  if (typeof value?.toDate === "function") {
    return value.toDate().toLocaleString();
  }

  if (typeof value === "string") {
    return new Date(value).toLocaleString();
  }

  return "-";
}

function sanitizeWhatsAppNumber(value) {
  return String(value || "").replace(/\D/g, "");
}

function normalizeIndianWhatsAppNumber(value) {
  const digits = sanitizeWhatsAppNumber(value);

  if (digits.length === 10) {
    return `91${digits}`;
  }

  if (digits.length === 12 && digits.startsWith("91")) {
    return digits;
  }

  return digits;
}

export function getEventOptions() {
  return EVENT_OPTIONS;
}

export function getEventOptionByValue(value) {
  return EVENT_OPTIONS.find((option) => option.value === value) || null;
}

function buildEventToken(prefix, nextNumber) {
  return `${prefix}${nextNumber}`;
}

function getNextEventTokenFromItems(items, eventType) {
  const eventOption = getEventOptionByValue(eventType);

  if (!eventOption) {
    throw new Error("Please select a valid event.");
  }

  const matchingItems = items.filter((item) => item.eventType === eventType);
  const nextNumber = matchingItems.length + 1;

  return {
    prefix: eventOption.prefix,
    eventLabel: eventOption.label,
    sequenceNumber: nextNumber,
    token: buildEventToken(eventOption.prefix, nextNumber),
  };
}

function getAdminNotificationConfig() {
  return {
    serviceId: process.env.REACT_APP_EMAILJS_SERVICE_ID,
    templateId: process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
    publicKey: process.env.REACT_APP_EMAILJS_PUBLIC_KEY,
    adminEmail: process.env.REACT_APP_ADMIN_NOTIFICATION_EMAIL || DEFAULT_ADMIN_NOTIFICATION_EMAIL,
  };
}

function formatAdminEnquiryEmailHtml(payload, adminEmail) {
  const createdAt = new Date().toLocaleString();

  return `
    <div style="font-family: Arial, sans-serif; color: #1f2937; line-height: 1.6;">
      <h2 style="margin-bottom: 16px;">New enquiry from Seekers Bay website</h2>
      <p>A new enquiry has been submitted. Here are the details:</p>
      <table style="border-collapse: collapse; width: 100%; max-width: 640px;">
        <tr><td style="padding: 8px; border: 1px solid #e5e7eb;"><strong>Source</strong></td><td style="padding: 8px; border: 1px solid #e5e7eb;">${payload.source || "website"}</td></tr>
        <tr><td style="padding: 8px; border: 1px solid #e5e7eb;"><strong>Parent Name</strong></td><td style="padding: 8px; border: 1px solid #e5e7eb;">${payload.parentName || "-"}</td></tr>
        <tr><td style="padding: 8px; border: 1px solid #e5e7eb;"><strong>Child Name</strong></td><td style="padding: 8px; border: 1px solid #e5e7eb;">${payload.childName || "-"}</td></tr>
        <tr><td style="padding: 8px; border: 1px solid #e5e7eb;"><strong>Child Age</strong></td><td style="padding: 8px; border: 1px solid #e5e7eb;">${payload.childAge || "-"}</td></tr>
        <tr><td style="padding: 8px; border: 1px solid #e5e7eb;"><strong>Phone Number</strong></td><td style="padding: 8px; border: 1px solid #e5e7eb;">${payload.phoneNumber || "-"}</td></tr>
        <tr><td style="padding: 8px; border: 1px solid #e5e7eb;"><strong>Email</strong></td><td style="padding: 8px; border: 1px solid #e5e7eb;">${payload.email || "-"}</td></tr>
        <tr><td style="padding: 8px; border: 1px solid #e5e7eb;"><strong>Program</strong></td><td style="padding: 8px; border: 1px solid #e5e7eb;">${payload.program || "-"}</td></tr>
        <tr><td style="padding: 8px; border: 1px solid #e5e7eb;"><strong>Submitted At</strong></td><td style="padding: 8px; border: 1px solid #e5e7eb;">${createdAt}</td></tr>
      </table>
      <p style="margin-top: 16px;">Notification recipient: ${adminEmail}</p>
    </div>
  `;
}

export async function sendAdminEnquiryNotification(payload) {
  const { serviceId, templateId, publicKey, adminEmail } = getAdminNotificationConfig();

  if (!serviceId || !templateId || !publicKey) {
    console.warn("Admin enquiry email skipped: missing EmailJS configuration.", {
      hasServiceId: Boolean(serviceId),
      hasTemplateId: Boolean(templateId),
      hasPublicKey: Boolean(publicKey),
      adminEmail,
      payload,
    });
    return { sent: false, skipped: true };
  }

  console.log("Sending admin enquiry email...", {
    adminEmail,
    payload,
  });

  const response = await fetch("https://api.emailjs.com/api/v1.0/email/send", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      service_id: serviceId,
      template_id: templateId,
      user_id: publicKey,
      template_params: {
        admin_email: adminEmail,
        to_email: adminEmail,
        recipient_email: adminEmail,
        to_name: "Seekers Bay Admin",
        from_name: payload.parentName || "Seekers Bay Website",
        reply_to: payload.email || "",
        source: payload.source || "website",
        parent_name: payload.parentName || "-",
        child_name: payload.childName || "-",
        child_age: payload.childAge || "-",
        phone_number: payload.phoneNumber || "-",
        email: payload.email || "-",
        program: payload.program || "-",
        submitted_at: new Date().toLocaleString(),
        message_html: formatAdminEnquiryEmailHtml(payload, adminEmail),
      },
    }),
  });

  if (!response.ok) {
    const errorText = await response.text();
    console.error("Admin enquiry email failed.", {
      adminEmail,
      status: response.status,
      errorText,
      payload,
    });
    throw new Error(errorText || "Unable to send admin email notification.");
  }

  console.log("Admin enquiry email sent successfully.", {
    adminEmail,
    payload,
  });
  return { sent: true, skipped: false };
}

async function notifyAdminSafely(payload) {
  return sendAdminEnquiryNotification(payload);
}

function formatWhatsAppEnquiryMessage(payload) {
  const lines = [
    "New enquiry from Seekers Bay website",
    `Source: ${payload.source || "website"}`,
    `Parent Name: ${payload.parentName || "-"}`,
    `Child Name: ${payload.childName || "-"}`,
    `Child Age: ${payload.childAge || "-"}`,
    `Phone Number: ${payload.phoneNumber || "-"}`,
    `Email: ${payload.email || "-"}`,
    `Program: ${payload.program || "-"}`,
  ];

  return lines.join("\n");
}

export async function getEnquiryWhatsAppUrl(payload) {
  const contactDetails = await fetchContactDetails();
  const phoneNumber = sanitizeWhatsAppNumber(contactDetails?.phone || defaultContactDetails.phone);

  if (!phoneNumber) {
    return "";
  }

  const encodedMessage = encodeURIComponent(formatWhatsAppEnquiryMessage(payload));
  return `https://wa.me/${phoneNumber}?text=${encodedMessage}`;
}

export async function sendEnquiryToWhatsApp(payload) {
  if (typeof window === "undefined") {
    return { opened: false, url: "" };
  }

  const whatsappUrl = await getEnquiryWhatsAppUrl(payload);

  if (!whatsappUrl) {
    return { opened: false, url: "" };
  }

  const popup = window.open(whatsappUrl, "_blank");
  return { opened: Boolean(popup), url: whatsappUrl };
}

function formatEventRegistrationWhatsAppMessage(payload) {
  const lines = [
    "Seekers Bay event registration confirmed",
    `Token: ${payload.token || "-"}`,
    `Event: ${payload.eventLabel || "-"}`,
    `Child Name: ${payload.childName || "-"}`,
    `Child Age: ${payload.childAge || "-"}`,
    `Parent WhatsApp Number: ${payload.parentNumber || "-"}`,
  ];

  return lines.join("\n");
}

export async function getEventRegistrationWhatsAppUrl(payload) {
  const phoneNumber = normalizeIndianWhatsAppNumber(payload?.parentNumber);

  if (!phoneNumber) {
    return "";
  }

  const encodedMessage = encodeURIComponent(formatEventRegistrationWhatsAppMessage(payload));
  return `https://wa.me/${phoneNumber}?text=${encodedMessage}`;
}

export async function sendEventRegistrationToWhatsApp(payload) {
  if (typeof window === "undefined") {
    return { opened: false, url: "" };
  }

  const whatsappUrl = await getEventRegistrationWhatsAppUrl(payload);

  if (!whatsappUrl) {
    return { opened: false, url: "" };
  }

  const popup = window.open(whatsappUrl, "_blank");
  return { opened: Boolean(popup), url: whatsappUrl };
}

export async function createEnquiry(payload) {
  if (!isFirebaseConfigured || !db) {
    const items = readLocalItems("popupSubmissions");
    const submission = { ...payload, createdAt: new Date().toISOString() };
    localStorage.setItem("popupSubmissions", JSON.stringify([submission, ...items]));
    await notifyAdminSafely(submission);
    return submission;
  }

  try {
    await addDoc(collection(db, "enquiries"), {
      ...payload,
      createdAt: serverTimestamp(),
    });
    await notifyAdminSafely(payload);
  } catch (error) {
    if (!isOfflineFirestoreError(error)) {
      throw error;
    }

    const items = readLocalItems("popupSubmissions");
    const submission = { ...payload, createdAt: new Date().toISOString() };
    localStorage.setItem("popupSubmissions", JSON.stringify([submission, ...items]));
    await notifyAdminSafely(submission);
    return submission;
  }
}

export async function createContactSubmission(payload) {
  if (!isFirebaseConfigured || !db) {
    const items = readLocalItems("contactSubmissions");
    const submission = { ...payload, createdAt: new Date().toISOString() };
    localStorage.setItem("contactSubmissions", JSON.stringify([submission, ...items]));
    await notifyAdminSafely(submission);
    return submission;
  }

  try {
    await addDoc(collection(db, "contactSubmissions"), {
      ...payload,
      createdAt: serverTimestamp(),
    });
    await notifyAdminSafely(payload);
  } catch (error) {
    if (!isOfflineFirestoreError(error)) {
      throw error;
    }

    const items = readLocalItems("contactSubmissions");
    const submission = { ...payload, createdAt: new Date().toISOString() };
    localStorage.setItem("contactSubmissions", JSON.stringify([submission, ...items]));
    await notifyAdminSafely(submission);
    return submission;
  }
}

export async function createEventRegistration(payload) {
  const eventOption = getEventOptionByValue(payload?.eventType);

  if (!eventOption) {
    throw new Error("Please select a valid event.");
  }

  if (!isFirebaseConfigured || !db) {
    const items = readLocalItems("eventRegistrations");
    const nextTokenInfo = getNextEventTokenFromItems(items, payload.eventType);
    const submission = {
      id: `local-event-${Date.now()}`,
      ...payload,
      eventLabel: eventOption.label,
      tokenPrefix: nextTokenInfo.prefix,
      tokenSequence: nextTokenInfo.sequenceNumber,
      token: nextTokenInfo.token,
      createdAt: new Date().toISOString(),
    };
    localStorage.setItem("eventRegistrations", JSON.stringify([submission, ...items]));
    return submission;
  }

  try {
    const submission = await runTransaction(db, async (transaction) => {
      const counterRef = doc(db, "siteContent", "eventTokenCounters");
      const counterSnapshot = await transaction.get(counterRef);
      const currentCounters = counterSnapshot.exists() ? counterSnapshot.data() : {};
      const nextNumber = Number(currentCounters[payload.eventType] || 0) + 1;
      const token = buildEventToken(eventOption.prefix, nextNumber);
      const registrationRef = doc(collection(db, "eventRegistrations"));

      transaction.set(
        counterRef,
        {
          [payload.eventType]: nextNumber,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      transaction.set(registrationRef, {
        ...payload,
        eventLabel: eventOption.label,
        tokenPrefix: eventOption.prefix,
        tokenSequence: nextNumber,
        token,
        createdAt: serverTimestamp(),
      });

      return {
        id: registrationRef.id,
        ...payload,
        eventLabel: eventOption.label,
        tokenPrefix: eventOption.prefix,
        tokenSequence: nextNumber,
        token,
      };
    });

    return submission;
  } catch (error) {
    if (!isOfflineFirestoreError(error)) {
      throw error;
    }

    const items = readLocalItems("eventRegistrations");
    const nextTokenInfo = getNextEventTokenFromItems(items, payload.eventType);
    const submission = {
      id: `local-event-${Date.now()}`,
      ...payload,
      eventLabel: eventOption.label,
      tokenPrefix: nextTokenInfo.prefix,
      tokenSequence: nextTokenInfo.sequenceNumber,
      token: nextTokenInfo.token,
      createdAt: new Date().toISOString(),
    };
    localStorage.setItem("eventRegistrations", JSON.stringify([submission, ...items]));
    return submission;
  }
}

export async function deleteEventRegistration(item) {
  if (!item) {
    return;
  }

  if (!isFirebaseConfigured || !db) {
    const items = readLocalItems("eventRegistrations");
    const nextItems = items.filter((entry) => {
      if (item.id && entry.id) {
        return entry.id !== item.id;
      }

      return !(
        entry.token === item.token &&
        entry.createdAt === item.createdAt &&
        entry.childName === item.childName
      );
    });
    localStorage.setItem("eventRegistrations", JSON.stringify(nextItems));
    return;
  }

  if (!item.id) {
    throw new Error("Unable to delete this event registration.");
  }

  await deleteDoc(doc(db, "eventRegistrations", item.id));
}

export async function fetchCollectionItems(name, sortField = "createdAt") {
  if (!isFirebaseConfigured || !db) {
    return readLocalItems(name);
  }

  try {
    const snapshot = await getDocs(query(collection(db, name), orderBy(sortField, "desc")));
    return snapshot.docs.map((item) => ({ id: item.id, ...item.data() }));
  } catch (error) {
    if (!isOfflineFirestoreError(error)) {
      throw error;
    }

    return readLocalItems(name);
  }
}

export async function fetchContactDetails() {
  if (!isFirebaseConfigured || !db) {
    return readLocalItems("contactDetails", defaultContactDetails);
  }

  try {
    const snapshot = await getDoc(doc(db, "siteContent", "contactDetails"));
    return snapshot.exists() ? { ...defaultContactDetails, ...snapshot.data() } : defaultContactDetails;
  } catch (error) {
    if (!isOfflineFirestoreError(error)) {
      throw error;
    }

    return readLocalItems("contactDetails", defaultContactDetails);
  }
}

export async function saveContactDetails(payload) {
  if (!isFirebaseConfigured || !db) {
    localStorage.setItem("contactDetails", JSON.stringify(payload));
    return payload;
  }

  await setDoc(
    doc(db, "siteContent", "contactDetails"),
    {
      ...payload,
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );
}

export async function fetchGalleryItems() {
  if (!isFirebaseConfigured || !db) {
    const items = readLocalItems("galleryImages");
    return items.length > 0 ? items.map(normalizeGalleryItem) : fallbackGalleryItems;
  }

  try {
    const snapshot = await getDocs(query(collection(db, "galleryImages"), orderBy("createdAt", "desc")));
    const items = snapshot.docs.map((item) => normalizeGalleryItem({ id: item.id, ...item.data() }));
    return items.length > 0 ? items : fallbackGalleryItems;
  } catch (error) {
    if (!isOfflineFirestoreError(error)) {
      throw error;
    }

    const items = readLocalItems("galleryImages");
    return items.length > 0 ? items.map(normalizeGalleryItem) : fallbackGalleryItems;
  }
}

export async function uploadGalleryImage({ file, title, category, mediaType = "image", videoUrl = "" }) {
  if (mediaType === "video") {
    const videoId = extractYouTubeVideoId(videoUrl);

    if (!videoId) {
      throw new Error("Please paste a valid YouTube link.");
    }

    const galleryItem = {
      title: title || "Gallery Video",
      category,
      mediaType: "video",
      videoUrl: videoUrl.trim(),
      videoId,
      embedUrl: `https://www.youtube.com/embed/${videoId}`,
      thumbnailUrl: `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`,
      createdAt: new Date().toISOString(),
    };

    if (!isFirebaseConfigured || !db) {
      const items = JSON.parse(localStorage.getItem("galleryImages") || "[]");
      const nextItem = {
        id: `local-${Date.now()}`,
        ...galleryItem,
      };
      localStorage.setItem("galleryImages", JSON.stringify([nextItem, ...items]));
      return nextItem;
    }

    const docRef = await addDoc(collection(db, "galleryImages"), {
      ...galleryItem,
      createdAt: serverTimestamp(),
    });

    return {
      id: docRef.id,
      ...galleryItem,
    };
  }

  if (!file) {
    throw new Error("Please select an image.");
  }

  const imageData = await fileToDataUrl(file);
  const galleryItem = {
    title: title || file.name,
    category,
    mediaType: "image",
    imageData,
    fileName: file.name,
    fileType: file.type,
    fileSize: file.size,
    hasImageData: true,
    createdAt: new Date().toISOString(),
  };

  if (!isFirebaseConfigured || !db) {
    const items = JSON.parse(localStorage.getItem("galleryImages") || "[]");
    const nextItem = {
      id: `local-${Date.now()}`,
      ...galleryItem,
    };
    localStorage.setItem("galleryImages", JSON.stringify([nextItem, ...items]));
    return nextItem;
  }

  const docRef = await addDoc(collection(db, "galleryImages"), {
    ...galleryItem,
    createdAt: serverTimestamp(),
  });

  return {
    id: docRef.id,
    ...galleryItem,
  };
}

export async function deleteGalleryImage(item) {
  if (!item?.id) {
    return;
  }

  if (!isFirebaseConfigured || !db) {
    const items = JSON.parse(localStorage.getItem("galleryImages") || "[]");
    localStorage.setItem(
      "galleryImages",
      JSON.stringify(items.filter((entry) => entry.id !== item.id))
    );
    return;
  }

  await deleteDoc(doc(db, "galleryImages", item.id));
}
